import type { StoryTime } from '../types/timeline';
import type { WorkspaceSettings } from '../types/workspace';
import { effectiveCalendarParts, formatCalendarDate } from './calendar';
import { calendarPositionDays, earthToCalendar } from './calendarConversion';

type CalendarConfig = WorkspaceSettings['calendar'];

function componentFor(config: CalendarConfig, role: 'day' | 'period' | 'cycle') {
  return config.components.find((component) => component.role === role);
}

function componentValue(component: NonNullable<ReturnType<typeof componentFor>>, numericValue: number) {
  return component.kind === 'ordered' ? component.values[numericValue - 1] ?? String(numericValue) : numericValue;
}

export function storyTimePosition(config: CalendarConfig, value: StoryTime | null) {
  if (!value) return null;
  const direct = calendarPositionDays(config, effectiveCalendarParts(value), value.clockLabel);
  if (direct !== null) return direct + (value.timeSource === 'relative' ? (value.relativeOffsetMinutes ?? 0) / (config.clock.hoursPerDay * config.clock.minutesPerHour) : 0);
  const converted = earthToCalendar(config, value.absoluteTime);
  return converted ? calendarPositionDays(config, converted.calendarParts, converted.clockLabel) : null;
}

export function storyTimeAtPosition(config: CalendarConfig, position: number): StoryTime | null {
  const periodsPerYear = config.periodsPerYear;
  if (!Number.isFinite(position) || !config.daysPerPeriod || !periodsPerYear) return null;
  const dayComponent = componentFor(config, 'day');
  const periodComponent = componentFor(config, 'period');
  const cycleComponent = componentFor(config, 'cycle');
  if (!dayComponent || !periodComponent || !cycleComponent) return null;
  const cycleDays = config.daysPerPeriod * periodsPerYear;
  let cycle = Math.floor(position / cycleDays);
  let withinCycle = position - cycle * cycleDays;
  if (withinCycle < 0) { cycle -= 1; withinCycle += cycleDays; }
  const dayIndex = Math.floor(withinCycle);
  const fraction = Math.max(0, withinCycle - dayIndex);
  const period = Math.floor(dayIndex / config.daysPerPeriod) + 1;
  const day = dayIndex % config.daysPerPeriod + 1;
  const localSecondsPerDay = config.clock.hoursPerDay * config.clock.minutesPerHour * config.clock.secondsPerMinute;
  const localSeconds = Math.min(localSecondsPerDay - 1, Math.round(fraction * localSecondsPerDay));
  const hours = Math.floor(localSeconds / (config.clock.minutesPerHour * config.clock.secondsPerMinute));
  const minutes = Math.floor(localSeconds / config.clock.secondsPerMinute) % config.clock.minutesPerHour;
  const seconds = localSeconds % config.clock.secondsPerMinute;
  const pad = (value: number) => String(value).padStart(2, '0');
  const clockLabel = `${pad(hours)}:${pad(minutes)}${config.clock.showSeconds ? `:${pad(seconds)}` : ''}`;
  const calendarParts = {
    [dayComponent.key]: componentValue(dayComponent, day),
    [periodComponent.key]: componentValue(periodComponent, period),
    [cycleComponent.key]: componentValue(cycleComponent, cycle)
  };
  return {
    calendarYear: null, calendarPeriod: null, calendarDay: null, calendarParts, clockLabel,
    timeBasis: 'calendar', absoluteTime: null, timeSource: 'explicit', narrativeRelation: 'normal',
    relativeOffsetMinutes: null, inheritedFromEntityId: null
  };
}

export function calendarAxisStep(config: CalendarConfig, scale: 'day' | 'week' | 'period' | 'cycle') {
  if (scale === 'cycle') return (config.daysPerPeriod ?? 1) * (config.periodsPerYear ?? 1);
  if (scale === 'period') return config.daysPerPeriod ?? 1;
  if (scale === 'week') return config.week?.dayNames.length ?? 1;
  return 1;
}

export function buildCalendarAxisTicks(config: CalendarConfig, center: number, scale: 'day' | 'week' | 'period' | 'cycle', count = 21) {
  const step = calendarAxisStep(config, scale);
  const normalizedCenter = Math.round(center / step) * step;
  const half = Math.floor(count / 2);
  return Array.from({ length: count }, (_, index) => {
    const position = normalizedCenter + (index - half) * step;
    const storyTime = storyTimeAtPosition(config, position);
    return { key: `${scale}:${position}`, position, storyTime, label: storyTime ? formatCalendarDate(config, storyTime, true) : String(position) };
  });
}
